import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, ArrowUpRight, ArrowDownLeft, MicrosoftExcelLogo,
  Trash, PencilSimple, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/reportExport";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";

const today = () => new Date().toISOString().slice(0, 10);

function emptyForm(direction) {
  return {
    direction,
    date: today(),
    customer_id: "",
    customer_name: "",
    customer_mobile: "",
    item_type: "other",
    item_id: "",
    item_name: "",
    dispatcher_name: "",
    dispatcher_mobile: "",
    reference: "",
    notes: "",
  };
}

function MovementForm({ direction, customers, products, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? { ...emptyForm(direction), ...editing } : emptyForm(direction));
  const [saving, setSaving] = useState(false);
  const [manualCustomer, setManualCustomer] = useState(!!editing?.customer_name && !editing?.customer_id);
  const [customItem, setCustomItem] = useState(!!editing?.item_name && !editing?.item_id);

  const pickCustomer = (id) => {
    if (id === "__manual__") { setManualCustomer(true); setF({ ...f, customer_id: "", customer_name: "", customer_mobile: "" }); return; }
    setManualCustomer(false);
    const c = customers.find((x) => x.id === id);
    setF({ ...f, customer_id: id, customer_name: c?.name || "", customer_mobile: c?.phone || "" });
  };
  const pickItem = (v) => {
    if (v === "__custom__") { setCustomItem(true); setF({ ...f, item_id: "", item_name: "", item_type: "other" }); return; }
    if (v === "none") { setCustomItem(false); setF({ ...f, item_id: "", item_name: "", item_type: "other" }); return; }
    setCustomItem(false);
    const p = products.find((x) => x.id === v);
    setF({ ...f, item_type: "product", item_id: v, item_name: p?.name || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!f.dispatcher_name.trim()) { toast.error("Dispatcher name is required"); return; }
    if (!f.dispatcher_mobile.trim()) { toast.error("Dispatcher mobile is required"); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/outward-inward/${editing.id}`, f);
        toast.success("Entry updated");
      } else {
        await api.post("/outward-inward", f);
        toast.success(`${direction === "outward" ? "Outward" : "Inward"} entry saved`);
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Date *</Label>
          <Input type="date" required value={f.date}
            onChange={(e) => setF({ ...f, date: e.target.value })}
            className="font-mono" data-testid="oi-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Reference / Docket #</Label>
          <Input value={f.reference}
            onChange={(e) => setF({ ...f, reference: e.target.value })}
            placeholder="AWB / Bill / Docket" className="font-mono" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Customer / Party *</Label>
        {!manualCustomer ? (
          <SearchableCustomerSelect
            value={f.customer_id}
            onChange={pickCustomer}
            customers={customers}
            createValue="__manual__"
            createLabel="Add manually…"
            placeholder="Choose customer / party"
            testid="oi-customer"
            getSubtitle={(c) => c.phone}
          />
        ) : (
          <div className="flex gap-2">
            <Input required value={f.customer_name}
              onChange={(e) => setF({ ...f, customer_name: e.target.value })}
              placeholder="Customer / party name"
              data-testid="oi-customer-name" />
            <Button type="button" variant="outline" className="h-9 px-2 text-[11px]"
              onClick={() => { setManualCustomer(false); setF({ ...f, customer_name: "", customer_mobile: "" }); }}>
              ← List
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Customer Mobile</Label>
        <Input inputMode="tel" value={f.customer_mobile}
          onChange={(e) => setF({ ...f, customer_mobile: e.target.value })}
          placeholder="10-digit mobile" data-testid="oi-customer-mobile" />
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Product / Other</Label>
        {!customItem ? (
          <Select value={f.item_id || "none"} onValueChange={pickItem}>
            <SelectTrigger data-testid="oi-item"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— none / not applicable —</SelectItem>
              <SelectItem value="__custom__" data-testid="oi-item-custom">+ Add custom item / other…</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex gap-2">
            <Input value={f.item_name}
              onChange={(e) => setF({ ...f, item_name: e.target.value, item_type: "other" })}
              placeholder="Item / Package / Document description" data-testid="oi-item-custom-input" />
            <Button type="button" variant="outline" className="h-9 px-2 text-[11px]"
              onClick={() => { setCustomItem(false); setF({ ...f, item_id: "", item_name: "" }); }}>
              ← List
            </Button>
          </div>
        )}
      </div>

      <div className="card-flat p-4 space-y-3 border-l-4"
        style={{ borderLeftColor: "hsl(var(--primary))" }}>
        <div className="eyebrow" style={{ color: "hsl(var(--primary))" }}>
          {direction === "outward" ? "Dispatcher Details" : "Receiver / Courier Details"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="eyebrow">Name *</Label>
            <Input required value={f.dispatcher_name}
              onChange={(e) => setF({ ...f, dispatcher_name: e.target.value })}
              placeholder="Person handling the item"
              data-testid="oi-dispatcher-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Mobile Number *</Label>
            <Input required inputMode="tel" value={f.dispatcher_mobile}
              onChange={(e) => setF({ ...f, dispatcher_mobile: e.target.value })}
              placeholder="10-digit mobile"
              data-testid="oi-dispatcher-mobile" />
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Notes</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="oi-save-btn">
          {saving ? "Saving…" : editing?.id ? "Update" : "Save Entry"}
        </Button>
      </div>
    </form>
  );
}

export default function OutwardInward() {
  const { activeCompanyId, activeCompany, can, activeStoreId } = useAuth();
  const [direction, setDirection] = useState("outward");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!activeCompanyId) return;
    Promise.all([
      api.get("/customers"),
      api.get("/products"),
    ]).then(([c, p]) => { setCustomers(c.data); setProducts(p.data); });
  }, [activeCompanyId, activeStoreId]);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ direction, q, ...(start ? { start } : {}), ...(end ? { end } : {}) });
      const { data } = await api.get(`/outward-inward?${p}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, direction, q, start, end]);

  const del = async (id) => {
    if (!confirm("Delete this entry?")) return;
    try { await api.delete(`/outward-inward/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Compose the outward/inward WhatsApp text. Directional verb + dispatcher info.
  const _composeMessage = (r) => {
    const compName = activeCompany?.name || "Company";
    const isOut = r.direction === "outward";
    const action = isOut ? "*DISPATCHED* to you" : "*RECEIVED* from you";
    const via = isOut ? "Dispatched via" : "Received via";
    const closing = isOut
      ? "Please confirm receipt when the item reaches you."
      : "Our team will process this and revert shortly.";
    return [
      `*${compName} — ${isOut ? "Dispatch" : "Receipt"} Confirmation*`,
      "",
      `Hi ${r.customer_name || "Customer"},`,
      "",
      `This is to confirm that the following item has been ${action} on *${r.date || "-"}*:`,
      "",
      `📦 Item: *${r.item_name || "Item / Package"}*`,
      `🚚 ${via}: *${r.dispatcher_name || "-"}*`,
      `📱 Contact: ${r.dispatcher_mobile || "-"}`,
      r.reference ? `🔖 Ref / Docket: ${r.reference}` : null,
      "",
      closing,
      "",
      `— ${compName}`,
    ].filter(Boolean).join("\n");
  };

  const sendWhatsApp = (r) => {
    const phone = String(r.customer_mobile || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("Customer mobile is empty — click edit and add it before sending WhatsApp.", { duration: 6000 });
      return;
    }
    const waPhone = phone.length === 10 ? `91${phone}` : phone;
    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(_composeMessage(r))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`WhatsApp opened for ${phone}`);
  };

  const sendEmail = async (r) => {
    try {
      const { data } = await api.post(`/outward-inward/${r.id}/email`, {});
      toast.success(`Email sent to ${data.sent_to}`, { duration: 5000 });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportExcel = () => {
    downloadCSV(`${direction}-register-${new Date().toISOString().slice(0,10)}`, [
      { key: "date", label: "Date" },
      { key: "customer_name", label: "Customer" },
      { key: "customer_mobile", label: "Customer Mobile" },
      { key: "item_name", label: "Item" },
      { key: "dispatcher_name", label: direction === "outward" ? "Dispatcher" : "Receiver / Courier" },
      { key: "dispatcher_mobile", label: "Dispatcher Mobile" },
      { key: "reference", label: "Ref / Docket" },
      { key: "notes", label: "Notes" },
    ], rows);
  };

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Register</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Outward &amp; Inward Register</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Log every dispatch (outward) and receipt (inward) with customer, product / item and dispatcher / courier details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" onClick={exportExcel} data-testid="oi-export">
            <MicrosoftExcelLogo size={16} className="mr-1.5" /> Excel
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="h-9" data-testid="oi-new-btn" onClick={() => setEditing(null)}>
                <Plus size={15} weight="bold" className="mr-1.5" />
                New {direction === "outward" ? "Outward" : "Inward"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
                  {direction === "outward"
                    ? <><ArrowUpRight size={20} className="text-primary" /> New Outward Entry</>
                    : <><ArrowDownLeft size={20} style={{ color: "hsl(var(--success))" }} /> New Inward Entry</>}
                </DialogTitle>
              </DialogHeader>
              <MovementForm direction={direction} customers={customers} products={products}
                editing={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={direction} onValueChange={setDirection}>
        <TabsList data-testid="oi-tabs">
          <TabsTrigger value="outward" data-testid="oi-tab-outward">
            <ArrowUpRight size={13} className="mr-1.5" /> Outward
          </TabsTrigger>
          <TabsTrigger value="inward" data-testid="oi-tab-inward">
            <ArrowDownLeft size={13} className="mr-1.5" /> Inward
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="card-flat p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[240px] flex-1">
          <Label className="eyebrow">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Customer, mobile, dispatcher or ref…" data-testid="oi-search" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">From</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)} className="font-mono" />
        </div>
        <Button variant="outline" className="h-9" onClick={() => { setQ(""); setStart(""); setEnd(""); }}>Reset</Button>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Cust. Mobile</th>
              <th>Item</th>
              <th>{direction === "outward" ? "Dispatcher" : "Receiver / Courier"}</th>
              <th>Dispatch Mobile</th>
              <th>Ref</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No {direction} entries.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`oi-row-${r.id}`}>
                <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                <td className="font-medium">{r.customer_name}</td>
                <td className="font-mono text-[12px]">{r.customer_mobile || "—"}</td>
                <td className="text-[12.5px]">
                  {r.item_name ? (
                    <><span className="pill pill-muted mr-1.5">{r.item_type}</span>{r.item_name}</>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td>{r.dispatcher_name}</td>
                <td className="font-mono text-[12px]">{r.dispatcher_mobile}</td>
                <td className="font-mono text-[12px] text-muted-foreground">{r.reference || "—"}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end items-center">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      style={{ color: r.customer_mobile ? "#128C7E" : "#94a3b8" }}
                      title={r.customer_mobile
                        ? `${r.direction === "outward" ? "WhatsApp dispatch confirmation" : "WhatsApp receipt confirmation"} → ${r.customer_mobile}`
                        : "No customer mobile on file — click edit to add"}
                      onClick={() => sendWhatsApp(r)}
                      data-testid={`oi-whatsapp-${r.id}`}>
                      <WhatsappLogo size={15} weight="fill" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      title={`Email ${r.direction === "outward" ? "dispatch" : "receipt"} confirmation to customer`}
                      onClick={() => sendEmail(r)}
                      data-testid={`oi-email-${r.id}`}>
                      <EnvelopeSimple size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => { setEditing(r); setOpen(true); }} data-testid={`oi-edit-${r.id}`}>
                      <PencilSimple size={13} />
                    </Button>
                    {can("outward_inward", "delete") && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => del(r.id)}>
                        <Trash size={13} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
